import {Box} from '@mui/material';
import {createFileRoute, redirect, useNavigate} from '@tanstack/react-router';
import {useEffect} from 'react';
import {useTolgee} from 'tolgee';

import {useFeatureFlags} from '~utils/featureFlags';
import {pushRoute, Route as RouteEnum} from '~utils/routeUtil';
import {useAreFeatureFlagsReady} from '~hooks/launchDarkly.hooks';
import {selectCurrentProjectId, selectCustomer} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {BaseLayout} from '~components/_layout/BaseLayout';
import {ModelProvider} from 'src/contexts/ModelProvider';
import {isEmbeddedAppConfigured} from 'src/embed/apps';
import {EmbeddedApp} from 'src/embed/EmbeddedApp';

export const Route = createFileRoute('/model/$modelId/measuring')({
  beforeLoad: () => {
    if (!isEmbeddedAppConfigured('measuring-app')) {
      throw redirect({to: RouteEnum.models as any});
    }
  },
  component: ModelMeasuringPage,
});

export function ModelMeasuringPage(): JSX.Element | null {
  const {modelId} = Route.useParams();
  const tolgee = useTolgee(['language']);
  const customer = useAppSelector(selectCustomer);
  const projectId = useAppSelector(selectCurrentProjectId);
  const {toggleEnableMeasuringApp} = useFeatureFlags();
  const areFeatureFlagsReady = useAreFeatureFlagsReady();
  const navigate = useNavigate();
  const isMeasuringAppHidden = areFeatureFlagsReady && !toggleEnableMeasuringApp;

  useEffect(() => {
    if (isMeasuringAppHidden) {
      navigate({to: RouteEnum.models as any});
    }
  }, [isMeasuringAppHidden, navigate]);

  if (!areFeatureFlagsReady || isMeasuringAppHidden || !customer || !projectId || !modelId) {
    return null;
  }

  return (
    <ModelProvider mode='model-route-param' modelId={modelId}>
      <BaseLayout fullscreen onClose={() => pushRoute(RouteEnum.models)}>
        <Box sx={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
          <EmbeddedApp
            key={modelId}
            appId='measuring-app'
            context={{customer, projectId, modelId, language: tolgee.getLanguage()}}
          />
        </Box>
      </BaseLayout>
    </ModelProvider>
  );
}
